import { Mic } from "lucide-react";
import { ouvrirMicro } from "@/components/shell/useDockAction";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PhoneCall, AlertTriangle, FileUp, ChevronRight } from "lucide-react";
import { api, messageErreur } from "@/lib/api";

const restant = (iso) => { if (!iso) return null; const ms = new Date(iso) - new Date(); if (ms <= 0) { const h = Math.ceil(-ms / 3600e3); return h >= 48 ? `dépassé de ${Math.floor(h / 24)} j` : `dépassé de ${h} h`; } return `${Math.floor(ms / 3600e3)} h restantes`; };

const Carte = ({ f, rouge, children }) => (
  <div className="at-card p-4 space-y-3" style={rouge ? { background: "color-mix(in srgb, var(--danger) 12%, var(--surface))", border: "2px solid var(--danger)" } : undefined} data-testid={`partner-file-${f.id}`}>
    <Link to={`/fiches/${f.id}`} className="flex items-center justify-between gap-2 min-h-[44px]" data-testid={`partner-file-open-${f.id}`}>
      <span className="min-w-0"><span className="font-bold text-lg block truncate">{f.contact_nom || f.nom}</span><span className="text-sm at-muted block truncate">{f.operation} · {f.commune}</span></span>
      <ChevronRight size={18} className="shrink-0" />
    </Link>
    {f.echeance_contact && !f.client_contacte_le && <p className="text-sm font-bold" style={{ color: rouge ? "var(--danger)" : "var(--ink)" }} data-testid={`partner-file-timer-${f.id}`}>{restant(f.echeance_contact)}</p>}
    {children}
  </div>
);

const Score = ({ s }) => (
  <section className="at-card p-4" data-testid="partner-score">
    <p className="font-bold mb-2">Mon score partenaire</p>
    <div className="grid grid-cols-2 gap-2 text-sm">
      {[["Délai moyen de prise en charge", s.delai_moyen_h === null ? "—" : `${s.delai_moyen_h} h`, "score-delay"], ["Fiches en retard", s.fiches_en_retard, "score-late"],
        ["Devis signés", s.taux_devis_signes === null ? "—" : `${s.taux_devis_signes} %`, "score-signed"], ["Satisfaction client", s.satisfaction ?? "—", "score-satisfaction"]].map(([l, v, t]) => (
        <div key={t} className="at-soft p-3 min-w-0"><p className="text-xs at-muted">{l}</p><p className="text-xl font-bold" data-testid={t}>{v}</p></div>
      ))}
    </div>
    {s.satisfaction === null && <p className="text-xs at-muted mt-2">Satisfaction : {s.satisfaction_note.toLowerCase()}.</p>}
  </section>
);

export const PostePartenaire = () => {
  const [d, setD] = useState(null);
  const charger = useCallback(() => api.get("/partenaire/accueil").then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const contacte = (f) => api.post(`/partenaire/${f.id}/client-contacte`).then(() => { toast.success("Client contacté : compteur arrêté. SMS de contrôle envoyé au client (simulation)."); charger(); }).catch((e) => toast.error(messageErreur(e)));
  const avancer = (f) => api.post(`/partenaire/${f.id}/avancer`).then((r) => { toast.success(`Dossier passé à « ${r.data.etape} ».`); charger(); }).catch((e) => toast.error(messageErreur(e)));
  if (!d) return <p className="at-muted" data-testid="partner-loading">Chargement de vos dossiers…</p>;
  const BoutonContacte = ({ f }) => <button type="button" onClick={() => contacte(f)} className="at-btn at-btn-primary w-full !min-h-[56px] text-base" data-testid={`client-contacted-${f.id}`}><PhoneCall size={18} />Client contacté</button>;
  return (
    <div className="space-y-4 mb-8 min-w-0" data-testid="partner-station">
      <button type="button" onClick={() => ouvrirMicro({ ecoute: true })} className="at-micro w-full min-h-[88px] flex items-center justify-center gap-3 text-lg font-bold" data-testid="partner-voice-button">
        <Mic size={30} />Dites ce qui a avancé
      </button>
      <p className="text-xs at-muted -mt-2">Ex. « installation terminée chez M. Le Goff » ou « visite chez Mme Kerbrat jeudi 14 h ». Vous validez, le commercial est prévenu.</p>
      {d.en_retard.length > 0 && (
        <section className="space-y-2" data-testid="partner-late">
          <p className="font-bold flex items-center gap-2" style={{ color: "var(--danger)" }}><AlertTriangle size={16} />En retard ({d.en_retard.length})</p>
          {d.en_retard.map((f) => <Carte key={f.id} f={f} rouge><BoutonContacte f={f} /></Carte>)}
        </section>
      )}
      <section className="space-y-2" data-testid="partner-new">
        <p className="font-bold">Nouveaux dossiers ({d.nouveaux.length})</p>
        {d.nouveaux.length === 0 && <p className="text-sm at-muted">Aucun nouveau dossier.</p>}
        {d.nouveaux.map((f) => <Carte key={f.id} f={f}><BoutonContacte f={f} /></Carte>)}
      </section>
      <section className="space-y-2" data-testid="partner-quotes-todo">
        <p className="font-bold">Devis à déposer ({d.devis_a_deposer.length})</p>
        {d.devis_a_deposer.map((f) => <Carte key={f.id} f={f}><Link to={`/fiches/${f.id}#devis`} className="at-btn at-btn-primary w-full !min-h-[56px] text-base" data-testid={`quote-upload-link-${f.id}`}><FileUp size={18} />Déposer le devis</Link></Carte>)}
      </section>
      {d.en_cours.length > 0 && (
        <section className="space-y-2" data-testid="partner-ongoing">
          <p className="font-bold">En cours ({d.en_cours.length})</p>
          {d.en_cours.map((f) => (
            <Carte key={f.id} f={f}>
              <p className="text-sm at-muted">Étape : {f.etape_label}</p>
              {f.action?.[0] === "avancer" ? <button type="button" onClick={() => avancer(f)} className="at-btn at-btn-primary w-full !min-h-[56px] text-base" data-testid={`partner-advance-${f.id}`}>{f.action[1]}</button>
                : <Link to={`/fiches/${f.id}#devis`} className="at-btn at-btn-ghost w-full" data-testid={`partner-quote-status-${f.id}`}>Statut du devis</Link>}
            </Carte>
          ))}
        </section>
      )}
      <Score s={d.score} />
    </div>
  );
};
